/**
 * Cashbox Detail Page
 *
 * Shows detailed information about a cashbox session including
 * financial summary like legacy system and all movements.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Receipt,
  FileText,
  CreditCard,
  Banknote,
  Calculator,
  RefreshCcw,
  Clock,
  User,
  Calendar,
  Lock,
  LockOpen,
  Wallet,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table'
import { PAYMENT_METHODS, type PaymentSummary } from './cashbox-constants'
import { PaymentMethodBreakdown } from './PaymentMethodBreakdown'

interface CashboxMovement {
  id: number
  movement_type: string
  amount: number
  description: string | null
  reference: string | null
  created_at: string | null
  user_id: number
}

interface CashboxDetail {
  id: number
  is_open: boolean
  opening_time: string | null
  closing_time: string | null
  initial_amount: number
  current_amount: number
  final_amount: number | null
  expected_amount: number
  expected_cash: number
  difference: number
  cash_difference: number
  total_sales: number
  total_income: number
  total_expense: number
  movements: CashboxMovement[]
  // Legacy financial breakdown
  subtotal_credit: number
  total_credit_sale_tax: number
  total_credit_discount: number
  total_credit: number
  subtotal_cash: number
  total_cash_sale_tax: number
  total_cash_discount: number
  total_cash: number
  subtotal_income: number
  total_income_sale_tax: number
  total_income_discount: number
  total_income_amount: number
  total_deposited: number
  total_devolutions: number
  opened_by_name: string | null
  closed_by_name: string | null
  close_notes: string | null
}

interface CashboxBill {
  id: number
  bill_number: string | null
  type: string
  status: string
  client_name: string | null
  subtotal: number
  total_sale_tax: number
  total: number
  pay_type: string | null
  created_at: string | null
}

interface CashboxBillsData {
  items: CashboxBill[]
  total: number
  total_cash: number
  total_credit: number
  total_amount: number
}

const billTypeLabels: Record<string, string> = {
  ticket: t('cashbox.billTypeTicket'),
  invoice: t('cashbox.billTypeInvoice'),
  proforma: t('cashbox.billTypeProforma'),
  credit_note: t('cashbox.billTypeCreditNote'),
}

const billStatusLabels: Record<string, { label: string; color: string }> = {
  pending: { label: t('cashbox.billStatusPending'), color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  paid: { label: t('cashbox.billStatusPaid'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  finalized: { label: t('cashbox.billStatusFinalized'), color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  cancelled: { label: t('cashbox.billStatusCancelled'), color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
}

const movementTypeLabels: Record<string, { label: string; color: string; icon: typeof TrendingUp }> = {
  sale: { label: t('cashbox.movementSale'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: ShoppingCart },
  income: { label: t('cashbox.movementIncome'), color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: TrendingUp },
  expense: { label: t('cashbox.movementExpense'), color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: TrendingDown },
  withdrawal: { label: t('cashbox.movementWithdrawal'), color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: TrendingDown },
  deposit: { label: t('cashbox.movementDeposit'), color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Receipt },
  devolution: { label: t('cashbox.movementDevolution'), color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: RefreshCcw },
}

export function CashboxDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: cashbox, isLoading, error } = useQuery({
    queryKey: ['cashbox-detail', id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${id}`)
      return response.data as CashboxDetail
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const { data: paymentSummary } = useQuery<PaymentSummary>({
    queryKey: ['cashbox-payment-summary', id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${id}/payment-summary`)
      return response.data
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const { data: billsData, isLoading: billsLoading } = useQuery({
    queryKey: ['cashbox-bills', id],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/${id}/bills`)
      return response.data as CashboxBillsData
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  if (isLoading) {
    return (
      <div className="space-y-4 pb-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-40" />
        <Skeleton className="h-60" />
        <Skeleton className="h-40" />
      </div>
    )
  }

  if (error || !cashbox) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-destructive mb-4">{t('common.error')}</p>
        <Button onClick={() => navigate('/cashbox/history')}>
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/cashbox/history')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('cashbox.cashboxNumber', { id: cashbox.id })}</h1>
          <Badge variant={cashbox.is_open ? 'success' : 'secondary'}>
            {cashbox.is_open ? t('cashbox.statusOpen') : t('cashbox.statusClosed')}
          </Badge>
        </div>
      </div>

      {/* Cashbox Session Info - Apertura / Cierre */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x">
            {/* Apertura */}
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-green-700 dark:text-green-400">
                <LockOpen className="h-4 w-4" />
                {t('cashbox.opening')}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.userLabel')}</span>
                  <span className="font-medium truncate">{cashbox.opened_by_name || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.dateLabel')}</span>
                  <span className="font-medium">
                    {cashbox.opening_time ? formatDateTime(cashbox.opening_time) : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.amountLabel')}</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(cashbox.initial_amount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Cierre */}
            <div className="p-4 space-y-3">
              <div className={`flex items-center gap-2 text-sm font-semibold ${
                cashbox.is_open
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-blue-700 dark:text-blue-400'
              }`}>
                <Lock className="h-4 w-4" />
                {t('cashbox.closing')}
                {cashbox.is_open && (
                  <Badge variant="warning" className="ml-auto text-xs">{t('cashbox.inProgress')}</Badge>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.userLabel')}</span>
                  <span className="font-medium truncate">{cashbox.closed_by_name || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.dateLabel')}</span>
                  <span className="font-medium">
                    {cashbox.closing_time ? formatDateTime(cashbox.closing_time) : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{t('cashbox.finalAmountLabel')}</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {cashbox.final_amount !== null ? formatCurrency(cashbox.final_amount) : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen del Día - Payment Method Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base md:text-lg">{t('cashbox.daySummary')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* Initial Amount */}
          <div className="flex justify-between items-center p-2.5 rounded-md bg-muted/50">
            <span className="text-sm text-muted-foreground">{t('cashbox.initialAmount')}</span>
            <span className="font-medium">{formatCurrency(cashbox.initial_amount)}</span>
          </div>

          {/* Cash Sales (Contado) with breakdown */}
          <PaymentMethodBreakdown
            title={t('cashbox.cashSalesTotal')}
            total={paymentSummary?.total_sales || 0}
            breakdown={paymentSummary?.sales_by_method}
            variant="cash"
            icon={TrendingUp}
            signed
          />

          {/* Credit Sales (informative — not in cash register) */}
          {paymentSummary && paymentSummary.total_credit_sales > 0 && (
            <PaymentMethodBreakdown
              title={t('cashbox.creditSalesEmitted')}
              total={paymentSummary.total_credit_sales}
              variant="credit"
              icon={Clock}
              hint={t('cashbox.creditSalesEmittedHint')}
            />
          )}

          {/* Deposits (Abonos) with breakdown */}
          {paymentSummary && paymentSummary.total_deposits > 0 && (
            <PaymentMethodBreakdown
              title={t('cashbox.creditDepositsTitle')}
              total={paymentSummary.total_deposits}
              breakdown={paymentSummary.deposits_by_method}
              variant="deposit"
              icon={TrendingUp}
              signed
            />
          )}

          {/* Devolutions */}
          {cashbox.total_devolutions > 0 && (
            <div className="flex justify-between items-center p-2.5 rounded-md border border-yellow-200 dark:border-yellow-800/40 bg-yellow-50/60 dark:bg-yellow-950/30">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <RefreshCcw className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400" />
                {t('cashbox.devolutions')}
              </span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                -{formatCurrency(cashbox.total_devolutions)}
              </span>
            </div>
          )}

          {/* Total Expenses */}
          {cashbox.total_expense > 0 && (
            <div className="flex justify-between items-center p-2.5 rounded-md border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-950/30">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
                {t('cashbox.totalExpense')}
              </span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                -{formatCurrency(cashbox.total_expense)}
              </span>
            </div>
          )}

          {/* Expected Balance */}
          <div className="flex justify-between items-center p-3 rounded-md bg-primary/10 mt-3">
            <span className="font-semibold">{t('cashbox.expectedBalance')}</span>
            <span className="font-bold text-lg text-primary">{formatCurrency(cashbox.expected_amount)}</span>
          </div>

          {/* Expected Cash in Register — with breakdown + closing comparison if closed */}
          {(() => {
            const closed = !cashbox.is_open && cashbox.final_amount !== null
            const diff = cashbox.cash_difference
            const isSobrante = closed && diff > 0
            const isFaltante = closed && diff < 0
            const isCuadrada = closed && diff === 0

            const resultTextClass = isFaltante
              ? 'text-amber-700 dark:text-amber-500'
              : isSobrante
              ? 'text-emerald-700 dark:text-emerald-400'
              : 'text-muted-foreground'

            return (
              <div className="rounded-md border overflow-hidden">
                {/* Header: Expected cash */}
                <div className="flex justify-between items-center p-3 bg-muted/40">
                  <span className="font-semibold flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                    {t('cashbox.expectedCashInRegister')}
                  </span>
                  <span className="font-bold text-lg">
                    {formatCurrency(paymentSummary?.expected_cash ?? cashbox.expected_cash)}
                  </span>
                </div>

                {/* Breakdown: viene del backend, el mismo cálculo del total */}
                <div className="border-t px-3 py-2 space-y-1 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('cashbox.opening')}</span>
                    <span className="tabular-nums font-medium">
                      +{formatCurrency(paymentSummary?.drawer_initial || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('cashbox.cashSalesInCash')}</span>
                    <span className="tabular-nums font-medium">
                      +{formatCurrency(paymentSummary?.drawer_cash_sales || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">{t('cashbox.depositsInCash')}</span>
                    <span className="tabular-nums font-medium">
                      +{formatCurrency(paymentSummary?.drawer_cash_deposits || 0)}
                    </span>
                  </div>
                  {(paymentSummary?.drawer_cash_devolutions || 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{t('cashbox.cashDevolutions')}</span>
                      <span className="tabular-nums font-medium">
                        -{formatCurrency(paymentSummary?.drawer_cash_devolutions || 0)}
                      </span>
                    </div>
                  )}
                  {(paymentSummary?.drawer_expenses || 0) > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{t('cashbox.expenses')}</span>
                      <span className="tabular-nums font-medium">
                        -{formatCurrency(paymentSummary?.drawer_expenses || 0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Closing comparison - only if closed */}
                {closed && (
                  <div className="border-t p-3 space-y-2 bg-muted/30">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('cashbox.countedAtClose')}</span>
                      <span className="font-semibold">
                        {formatCurrency(cashbox.final_amount || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className={`font-semibold flex items-center gap-2 ${resultTextClass}`}>
                        {isFaltante ? (
                          <AlertTriangle className="h-4 w-4" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4" />
                        )}
                        {isCuadrada
                          ? t('cashbox.noDifference')
                          : isSobrante
                          ? t('cashbox.surplusInBox')
                          : t('cashbox.shortageInBox')}
                      </span>
                      <span className={` font-bold text-lg ${resultTextClass}`}>
                        {isSobrante ? '+' : ''}{formatCurrency(diff)}
                      </span>
                    </div>
                    {cashbox.close_notes && (
                      <div className="pt-2 border-t text-xs">
                        <p className="text-muted-foreground mb-0.5">{t('cashbox.reasonLabel')}</p>
                        <p className="font-medium whitespace-pre-wrap">{cashbox.close_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })()}
        </CardContent>
      </Card>

      {/* INGRESOS Section - Like Legacy */}
      <Card>
        <CardHeader className="pb-2 bg-blue-50 dark:bg-blue-900/20">
          <CardTitle className="text-lg flex items-center gap-2 text-blue-700 dark:text-blue-400">
            <Calculator className="h-5 w-5" />
            {t('cashbox.ingresos')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* CREDITO */}
          <div className="border-b">
            <div className="bg-orange-50 dark:bg-orange-900/20 px-4 py-2 font-medium text-orange-700 dark:text-orange-400">
              {t('cashbox.creditSection')}
            </div>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="w-1/2">{t('cashbox.subtotal')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.subtotal_credit)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.discount')}</TableCell>
                  <TableCell className="text-right text-red-600 dark:text-red-400">
                    -{formatCurrency(cashbox.total_credit_discount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.taxIva')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.total_credit_sale_tax)}</TableCell>
                </TableRow>
                <TableRow className="bg-orange-50 dark:bg-orange-900/20">
                  <TableCell className="font-bold">{t('cashbox.total')}</TableCell>
                  <TableCell className="text-right font-bold text-orange-700 dark:text-orange-400">
                    {formatCurrency(cashbox.total_credit)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* CONTADO */}
          <div className="border-b">
            <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 font-medium text-green-700 dark:text-green-400">
              {t('cashbox.cashSection')}
            </div>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="w-1/2">{t('cashbox.subtotal')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.subtotal_cash)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.discount')}</TableCell>
                  <TableCell className="text-right text-red-600 dark:text-red-400">
                    -{formatCurrency(cashbox.total_cash_discount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.taxIva')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.total_cash_sale_tax)}</TableCell>
                </TableRow>
                <TableRow className="bg-green-50 dark:bg-green-900/20">
                  <TableCell className="font-bold">{t('cashbox.total')}</TableCell>
                  <TableCell className="text-right font-bold text-green-700 dark:text-green-400">
                    {formatCurrency(cashbox.total_cash)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* TOTAL VENTAS */}
          <div className="border-b">
            <div className="bg-blue-100 dark:bg-blue-900/20 px-4 py-2 font-medium text-blue-700 dark:text-blue-400">
              {t('cashbox.totalSalesSection')}
            </div>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="w-1/2">{t('cashbox.subtotal')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.subtotal_income)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.discount')}</TableCell>
                  <TableCell className="text-right text-red-600 dark:text-red-400">
                    -{formatCurrency(cashbox.total_income_discount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('cashbox.taxIva')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(cashbox.total_income_sale_tax)}</TableCell>
                </TableRow>
                <TableRow className="bg-blue-100 dark:bg-blue-900/20">
                  <TableCell className="font-bold">{t('cashbox.total')}</TableCell>
                  <TableCell className="text-right font-bold text-blue-700 dark:text-blue-400">
                    {formatCurrency(cashbox.total_income_amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* RECIBO DE DINERO */}
          {(() => {
            const totalDeposited = paymentSummary?.total_deposits ?? cashbox.total_deposited
            const depositsBreakdown = paymentSummary?.deposits_by_method
            return (
              <div className="border-b">
                <div className="bg-purple-50 dark:bg-purple-900/20 px-4 py-2 font-medium text-purple-700 dark:text-purple-400">
                  {t('cashbox.moneyReceiptSection')}
                </div>
                <Table>
                  <TableBody>
                    {depositsBreakdown &&
                      PAYMENT_METHODS.map((method) => {
                        const amount = depositsBreakdown[method.key] || 0
                        if (amount < 0.01) return null
                        const MethodIcon = method.icon
                        return (
                          <TableRow key={method.key}>
                            <TableCell className="w-1/2 pl-8 text-muted-foreground flex items-center gap-1.5">
                              <MethodIcon className="h-3.5 w-3.5" />
                              {method.label}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(amount)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    <TableRow className="bg-purple-50 dark:bg-purple-900/20">
                      <TableCell className="w-1/2 font-bold">{t('cashbox.depositsSection')}</TableCell>
                      <TableCell className="text-right font-bold text-purple-700 dark:text-purple-400">
                        {formatCurrency(totalDeposited)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )
          })()}

          {/* TOTAL INGRESOS */}
          {(() => {
            const totalDeposited = paymentSummary?.total_deposits ?? cashbox.total_deposited
            return (
              <div>
                <div className="bg-primary/20 px-4 py-2 font-medium text-primary">
                  {t('cashbox.totalIncomeSection')}
                </div>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="w-1/2">{t('cashbox.cashSalesSection')}</TableCell>
                      <TableCell className="text-right">{formatCurrency(cashbox.total_cash)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{t('cashbox.depositsSection')}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalDeposited)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="flex items-center gap-1">
                        <RefreshCcw className="h-4 w-4" />
                        {t('cashbox.devolutionsSection')}
                      </TableCell>
                      <TableCell className="text-right text-red-600 dark:text-red-400">
                        -{formatCurrency(cashbox.total_devolutions)}
                      </TableCell>
                    </TableRow>
                    <TableRow className="bg-primary/20">
                      <TableCell className="font-bold text-lg">{t('cashbox.total')}</TableCell>
                      <TableCell className="text-right font-bold text-primary text-xl">
                        {formatCurrency(cashbox.total_cash + totalDeposited - cashbox.total_devolutions)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )
          })()}
        </CardContent>
      </Card>

      {/* Movements */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('cashbox.movements')}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {cashbox.movements.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {t('cashbox.noMovements')}
            </p>
          ) : (
            <div className="space-y-2">
              {cashbox.movements.map((movement) => {
                const typeInfo = movementTypeLabels[movement.movement_type] || {
                  label: movement.movement_type,
                  color: 'bg-gray-100 text-gray-700',
                  icon: Receipt,
                }
                const Icon = typeInfo.icon

                const isIncome = ['sale', 'income', 'deposit'].includes(movement.movement_type)

                return (
                  <div
                    key={movement.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                  >
                    <div className={`p-2 rounded-full ${typeInfo.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${typeInfo.color}`}>
                          {typeInfo.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {movement.description || '-'}
                      </p>
                      {movement.created_at && (
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(movement.created_at)}
                        </p>
                      )}
                    </div>
                    <p className={`font-bold shrink-0 ${isIncome ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {isIncome ? '+' : '-'}{formatCurrency(movement.amount)}
                    </p>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bills / Facturas */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('cashbox.bills')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {billsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          ) : !billsData || billsData.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {t('cashbox.noBills')}
            </p>
          ) : (
            <>
              {/* Bills Summary */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Banknote className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.cashSection')}</p>
                  <p className="font-bold text-green-600">{formatCurrency(billsData.total_cash)}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CreditCard className="h-4 w-4 text-orange-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.creditSection')}</p>
                  <p className="font-bold text-orange-600">{formatCurrency(billsData.total_credit)}</p>
                </div>
                <div className="bg-primary/10 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('cashbox.total')}</p>
                  <p className="font-bold text-primary">{formatCurrency(billsData.total_amount)}</p>
                </div>
              </div>

              {/* Bills List */}
              <div className="space-y-2">
                {billsData.items.map((bill) => {
                  const statusInfo = billStatusLabels[bill.status] || {
                    label: bill.status,
                    color: 'bg-gray-100 text-gray-700',
                  }

                  return (
                    <div
                      key={bill.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/bills/${bill.id}`)}
                    >
                      <div className="p-2 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            #{bill.id}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {billTypeLabels[bill.type] || bill.type}
                          </Badge>
                          {/* "credit" no tenía etiqueta y salía crudo, repitiendo el chip de Crédito. */}
                          {bill.status !== 'credit' && (
                            <Badge variant="outline" className={`text-xs ${statusInfo.color}`}>
                              {statusInfo.label}
                            </Badge>
                          )}
                          {bill.pay_type === 'credit' && (
                            <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                              {t('cashbox.creditSection')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {bill.client_name || t('cashbox.generalClient')}
                        </p>
                        {bill.created_at && (
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(bill.created_at)}
                          </p>
                        )}
                      </div>
                      <p className="font-bold text-primary shrink-0">
                        {formatCurrency(bill.total)}
                      </p>
                    </div>
                  )
                })}
              </div>

              <p className="text-center text-sm text-muted-foreground mt-3">
                {billsData.total} factura{billsData.total !== 1 ? 's' : ''} en total
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
